import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import empApi from "../lib/empApi";
import { formatIQD, formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import { LogOut, UtensilsCrossed, Wallet, AlertCircle, Receipt } from "lucide-react";

export default function EmployeePortal() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);

  const logout = () => { localStorage.removeItem("emp_token"); nav("/portal/login"); };

  useEffect(() => {
    empApi.get("/employee-portal/summary").then((r) => setData(r.data)).catch(() => nav("/portal/login"));
    empApi.get("/employee-portal/transactions").then((r) => setTxns(r.data));
  }, [nav]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  const e = data.employee;

  return (
    <div className="min-h-screen bg-background" data-testid="employee-portal">
      <header className="h-16 border-b border-border bg-white/80 dark:bg-slate-900/60 backdrop-blur-md flex items-center justify-between px-6 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center text-white">
            <UtensilsCrossed size={16} />
          </div>
          <div>
            <div className="heading text-lg font-bold">Employee Portal</div>
            <div className="text-xs text-muted-foreground">Signed in as {e.name}</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} data-testid="btn-emp-logout"><LogOut size={14} className="mr-1" />Log out</Button>
      </header>
      <main className="max-w-5xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="heading text-2xl font-extrabold">Welcome, {e.name}</h1>
          <p className="text-sm text-muted-foreground">Your food orders, payments, and current balance.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-5 kpi-card">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Total Orders</div>
            <div className="mt-2 heading text-2xl font-extrabold">{data.total_orders}</div>
            <div className="text-xs text-muted-foreground mt-1"><UtensilsCrossed size={12} className="inline mr-1" />Food orders</div>
          </Card>
          <Card className="p-5 kpi-card">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Total Food Cost</div>
            <div className="mt-2 heading text-2xl font-extrabold">{formatIQD(data.total_cost)}</div>
            <div className="text-xs text-muted-foreground mt-1"><Receipt size={12} className="inline mr-1" />Amount ordered</div>
          </Card>
          <Card className="p-5 kpi-card">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Total Paid</div>
            <div className="mt-2 heading text-2xl font-extrabold text-emerald-700">{formatIQD(data.total_paid)}</div>
            <div className="text-xs text-muted-foreground mt-1"><Wallet size={12} className="inline mr-1" />You have paid</div>
          </Card>
          <Card className="p-5 kpi-card border-rose-200">
            <div className="text-xs uppercase text-rose-700 tracking-wider">Current Outstanding</div>
            <div className="mt-2 heading text-2xl font-extrabold text-rose-700" data-testid="emp-outstanding">{formatIQD(data.outstanding)}</div>
            <div className="text-xs text-muted-foreground mt-1"><AlertCircle size={12} className="inline mr-1" />{data.unpaid_orders_count} unpaid orders</div>
          </Card>
        </div>
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b heading font-bold">Transaction History</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Description</th>
                  <th className="p-3 text-right">Amount</th><th className="p-3 text-right">Balance</th><th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={`${t.type}-${t.id}`} className="border-t table-row-hover">
                    <td className="p-3">{formatDate(t.date)}</td>
                    <td className="p-3">{t.type === "order" ? <span className="text-rose-700 font-medium">Food Order</span> : <span className="text-emerald-700 font-medium">Payment</span>}</td>
                    <td className="p-3">{t.description}{t.restaurant ? ` · ${t.restaurant}` : ""}</td>
                    <td className={`p-3 text-right mono ${t.type === "order" ? "text-rose-700" : "text-emerald-700"}`}>{t.type === "order" ? "+" : "-"}{formatIQD(t.amount)}</td>
                    <td className="p-3 text-right mono font-semibold">{formatIQD(t.balance_after)}</td>
                    <td className="p-3">{t.status ? <StatusBadge status={t.status} /> : "—"}</td>
                  </tr>
                ))}
                {txns.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No transactions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
}
